// ============================================================================
// TeamBadgesAside — aside « Distinctions du mois » (E1b(c), F-193)
// ============================================================================
// Deferee en E1b faute de computeTeamBadges ; DEBLOQUEE par L10 (le MASTER
// autorise explicitement a toucher EquipesPage pour la cabler). Aside SIMPLE :
// consomme computeTeamBadges (lib pure, deja portee ET testee) sur les pulses/
// objectifs deja charges par GET /api/teams. Aucun calcul local, aucun fetch.
//
// ANTI-FUITE M-208 : les `badgePulses` du serveur portent la LONGUEUR du narratif,
// jamais son texte — /equipes est ouvert a tout le management (manager inclus)
// alors que M-208 reserve le narratif a admin OU au directeur de CHAQUE equipe.
// La longueur suffit au score `communication`, ne revele rien, et rend le palmares
// IDENTIQUE pour tous les viewers (n'envoyer que le narratif autorise donnerait un
// gagnant different selon qui regarde, donc faux).
//
// i18n : `label`/`description` viennent des cles par `key` (le module lib renvoie
// des libelles FR en dur — on ne les affiche pas). `metricLabel` est en revanche
// rendu TEL QUEL : c'est une chaine calculee par la lib portee (« 4/6 mois »,
// « 3 partages détaillés », « 4,2/5 d’humeur »), la reformater en i18n reviendrait
// a re-implementer la lib — consigne au rapport (NT).

import { Award, MessageSquare, Target, Smile } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { computeTeamBadges, type BadgeKey, type BadgeTeamInput } from '../../lib/team/badges';
import type { TeamSummary } from '../../hooks/useTeams';

/** key -> icone (le module lib reste testable sans dependance UI, par design). */
const BADGE_ICONS: Record<BadgeKey, LucideIcon> = {
  assiduity: Award,
  communication: MessageSquare,
  objectives: Target,
  climate: Smile,
};

export function TeamBadgesAside({
  teams,
  currentPeriod,
}: {
  teams: TeamSummary[];
  currentPeriod: string;
}) {
  const { t } = useTranslation();

  const inputs: BadgeTeamInput[] = teams.map((team) => ({
    teamId: team.id,
    teamName: team.name,
    objectivesDone: team.objectivesDone,
    pulses: (team.badgePulses ?? []).map((p) => ({
      period: p.period,
      mood_score: p.moodScore,
      // Le texte n'arrive JAMAIS au client (M-208) : la lib utilise
      // narrative_length quand il est fourni.
      challenges: null,
      positives: null,
      support_needed: null,
      narrative_length: p.narrativeLength,
    })),
  }));

  const badges = computeTeamBadges(inputs, { currentPeriod });

  // M-172 : une distinction sans gagnant (aucune equipe eligible) n'est pas rendue ;
  // si aucune n'a de gagnant, l'aside disparait plutot que d'afficher 4 vides.
  const awarded = badges.filter((b) => b.winner !== null);
  if (awarded.length === 0) return null;

  return (
    <aside className="rounded-xl bg-white p-5 shadow-xs dark:bg-slate-800">
      <h2 className="mb-3 flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
        <Award size={18} aria-hidden="true" className="text-brand-500" />
        {t('gest:equipes.badges.title')}
      </h2>
      <ul className="space-y-3">
        {awarded.map((badge) => {
          const Icon = BADGE_ICONS[badge.key];
          return (
            <li key={badge.key} className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-950">
                <Icon size={16} aria-hidden="true" className="text-brand-600 dark:text-brand-300" />
              </span>
              <div className="min-w-0">
                <p className="text-field font-medium text-slate-900 dark:text-slate-100">
                  {t(`gest:equipes.badges.label.${badge.key}`)}
                </p>
                <p className="truncate text-meta text-slate-500 dark:text-slate-400">
                  {badge.winner!.teamName}
                  {badge.metricLabel ? ` · ${badge.metricLabel}` : ''}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
